import { X, Download, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { generateDocx } from "@/utils/docxGenerator";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";

const ProductBadge = ({ product }) => {
  const colorClasses = {
    "DataHub": "bg-emerald-50 text-emerald-700 border-emerald-200",
    "AI Forecasts": "bg-violet-50 text-violet-700 border-violet-200",
    "Dispatch Solutions": "bg-orange-50 text-orange-700 border-orange-200",
    "Emission Intensity Forecasting": "bg-teal-50 text-teal-700 border-teal-200",
    "Signal Orchestrator": "bg-indigo-50 text-indigo-700 border-indigo-200",
  };

  return (
    <span className={cn(
      "inline-flex items-center px-2.5 py-1 text-xs font-semibold rounded-full border",
      colorClasses[product] || "bg-slate-100 text-slate-700 border-slate-200"
    )}>
      {product}
    </span>
  );
};

const UseCaseModal = ({ useCase, onClose, onDownload, onEdit, onDelete }) => {
  const { isAdmin } = useAuth();
  const handleDownload = async () => {
    await generateDocx(useCase);
    onDownload(useCase);
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 modal-backdrop"
      onClick={handleBackdropClick}
      data-testid="usecase-modal"
    >
      <div className="relative w-full max-w-3xl max-h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="sticky top-0 bg-white z-10 px-6 py-4 border-b border-slate-100">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-slate-900 font-['Manrope'] pr-8">
                {useCase.title}
              </h2>
              <div className="flex flex-wrap gap-2 mt-3">
                {useCase.product.map((prod) => (
                  <ProductBadge key={prod} product={prod} />
                ))}
                {useCase.market.map((mkt) => (
                  <span key={mkt} className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full bg-sky-50 text-sky-700 border border-sky-200">
                    {mkt}
                  </span>
                ))}
                {useCase.industry.map((ind) => (
                  <span key={ind} className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full bg-slate-100 text-slate-600 border border-slate-200">
                    {ind}
                  </span>
                ))}
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-full hover:bg-slate-100 flex items-center justify-center transition-colors flex-shrink-0"
              data-testid="modal-close-btn"
            >
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>
        </div>

        {/* Content */}
        <ScrollArea className="h-[calc(90vh-200px)]">
          <div className="p-6">
            <Tabs defaultValue="overview" className="w-full">
              <TabsList className="grid w-full grid-cols-5 mb-6 bg-slate-100 p-1 rounded-lg">
                <TabsTrigger 
                  value="overview" 
                  className="text-sm data-[state=active]:bg-white data-[state=active]:text-[#FF6F40] data-[state=active]:shadow-sm rounded-md"
                >
                  Overview
                </TabsTrigger>
                <TabsTrigger 
                  value="pain-points"
                  className="text-sm data-[state=active]:bg-white data-[state=active]:text-[#FF6F40] data-[state=active]:shadow-sm rounded-md"
                >
                  Pain Points
                </TabsTrigger>
                <TabsTrigger 
                  value="solution"
                  className="text-sm data-[state=active]:bg-white data-[state=active]:text-[#FF6F40] data-[state=active]:shadow-sm rounded-md"
                >
                  Solution
                </TabsTrigger>
                <TabsTrigger 
                  value="value"
                  className="text-sm data-[state=active]:bg-white data-[state=active]:text-[#FF6F40] data-[state=active]:shadow-sm rounded-md"
                >
                  Value
                </TabsTrigger>
                <TabsTrigger 
                  value="technical"
                  className="text-sm data-[state=active]:bg-white data-[state=active]:text-[#FF6F40] data-[state=active]:shadow-sm rounded-md"
                >
                  Technical
                </TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="mt-0">
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-2">Summary</h4>
                    <p className="text-slate-700 leading-relaxed">{useCase.summary}</p>
                  </div>
                  {useCase.tags.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-2">Tags</h4>
                      <div className="flex flex-wrap gap-2">
                        {useCase.tags.map((tag) => (
                          <span key={tag} className="px-2.5 py-1 bg-slate-100 text-slate-600 text-xs rounded-full">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="pain-points" className="mt-0">
                <div>
                  <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">Client Pain Points</h4>
                  <ul className="space-y-3">
                    {useCase.painPoints.map((point, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-6 h-6 bg-red-100 text-red-600 rounded-full flex items-center justify-center text-xs font-medium">
                          {index + 1}
                        </span>
                        <span className="text-slate-700">{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </TabsContent>

              <TabsContent value="solution" className="mt-0">
                <div>
                  <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">Arcus Power Solution</h4>
                  <p className="text-slate-700 leading-relaxed">{useCase.solution}</p>
                </div>
              </TabsContent>

              <TabsContent value="value" className="mt-0">
                <div>
                  <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">Value Proposition</h4>
                  <ul className="space-y-3">
                    {useCase.valueProposition.map((value, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-6 h-6 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center text-xs font-medium">
                          {index + 1}
                        </span>
                        <span className="text-slate-700">{value}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </TabsContent>

              <TabsContent value="technical" className="mt-0">
                <div>
                  <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">Technical Context</h4>
                  {useCase.technicalContext ? (
                    <p className="text-slate-700 leading-relaxed">{useCase.technicalContext}</p>
                  ) : (
                    <p className="text-slate-400 italic">No technical context provided for this use case.</p>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </ScrollArea>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-slate-100 px-6 py-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-400 italic">
              This use case was developed for: <span className="text-slate-600 font-medium not-italic">{useCase.clientNote}</span>
            </p>
            <div className="flex items-center gap-2">
              {isAdmin && (
                <>
                  <Button
                    variant="outline"
                    onClick={() => onDelete(useCase)}
                    className="text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
                    data-testid="modal-delete-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => onEdit(useCase)}
                    className="text-slate-700 hover:bg-slate-50"
                    data-testid="modal-edit-btn"
                  >
                    <Pencil className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                </>
              )}
              <Button
                onClick={handleDownload}
                className="bg-[#FF6F40] hover:bg-[#E65A2C] text-white font-semibold"
                data-testid="modal-download-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Download DOCX
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UseCaseModal;
